import { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  LogOut, Search, Car, Home, Heart, Building2, FileText, Clock, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, Eye, RefreshCw, Inbox, Tag, Plus, Trash2, CreditCard, Calendar,
  UserCheck, X, Users, Briefcase, PartyPopper, Plane, Shield, ShieldAlert, ShieldCheck, Stethoscope, UserPlus, UserMinus, Hand, Star, Instagram, Send, FileDown, Plug, Newspaper, FileSignature, Activity, Flame, TrendingDown, TrendingUp, DollarSign, MapPin, Store, ListChecks, Database, Upload, Sparkles
} from "lucide-react";
import WhistleblowingAdminTab from "@/components/WhistleblowingAdminTab";
import StimaRicostruzioneTab from "@/components/admin/StimaRicostruzioneTab";
import MyRichiesteWidget from "@/components/admin/MyRichiesteWidget";
// v1.14.59 — Import rimosso: import RichiesteCommercioTab from "@/components/admin/RichiesteCommercioTab";
import VrsDetailBlock from "@/components/admin/VrsDetailBlock";
import RichiesteAssicurativeTab from "@/components/RichiesteAssicurativeTab";
import RisTariffaHealth from "@/components/admin/RisTariffaHealth";
import AdminCockpitPage from "@/pages/admin/AdminCockpitPage";
import CRMLeadsPage from "@/pages/admin/CRMRichiestePage";
import CRMRichiesteUnifiedPage from "@/pages/admin/CRMRichiesteUnifiedPage";
import CRMCustomersPage from "@/pages/admin/CRMCustomersPage";
import CRMDashboardPage from "@/pages/admin/CRMDashboardPage";
import DOQuestionnaireFormPage from "@/pages/admin/DOQuestionnaireFormPage";
import DOInvitesPanel from "@/pages/admin/DOInvitesPanel";
import CRMPoliciesPage from "@/pages/admin/CRMPoliciesPage";
import CRMRenewalsPage from "@/pages/admin/CRMRenewalsPage";
import CRMTasksPage from "@/pages/admin/CRMTasksPage";
import CRMIntermediariPage from "@/pages/admin/CRMIntermediariPage";
import CRMCompagniePartnerPage from "@/pages/admin/CRMCompagniePartnerPage";
import CRMEventiPage from "@/pages/admin/CRMEventiPage";
import CRMInsolutiPage from "@/pages/admin/CRMInsolutiPage";
import CRMDocumentsPage from "@/pages/admin/CRMDocumentsPage";
import CRMPaymentsPage from "@/pages/admin/CRMPaymentsPage";
import CRMCommunicationsPage from "@/pages/admin/CRMCommunicationsPage";
import AccountSecurityPage from "@/pages/admin/AccountSecurityPage";
import ComplianceAuditPage from "@/pages/admin/ComplianceAuditPage";
import AdminAISiteDoctorPage from "@/pages/admin/AdminAISiteDoctorPage";
import CRMConsentsPage from "@/pages/admin/CRMConsentsPage";
import ConvenzioniLeadsTab from "@/components/ConvenzioniLeadsTab";
import GbpSyncTab from "@/components/GbpSyncTab";
import PraticheNoleggioTab from "@/components/PraticheNoleggioTab";
import PraticheCreditoTab from "@/components/PraticheCreditoTab";
import NewsNoleggioTab from "@/components/NewsNoleggioTab";
import RivoluzioneAdminTab from "@/components/RivoluzioneAdminTab";
import InstagramPublishTab from "@/components/InstagramPublishTab";
import CaroselloExportTab from "@/components/CaroselloExportTab";
import AdminSocialGeneratorPage from "@/pages/admin/AdminSocialGeneratorPage";
import AdminAIAssistantPage from "@/pages/admin/AdminAIAssistantPage";
import AdminQuoterPage from "@/pages/admin/AdminQuoterPage";
import NexiTransactionsTab from "@/components/NexiTransactionsTab";
import PolizzaAISyncTab from "@/components/PolizzaAISyncTab";
import RealeMutuaDocsTab from "@/components/RealeMutuaDocsTab";
import PaymentLinkGenerator from "@/components/PaymentLinkGenerator";
import TwoFactorSettings from "@/components/TwoFactorSettings";
import SiteAccessGateToggle from "@/components/SiteAccessGateToggle";
import LiveTrafficWidget from "@/components/LiveTrafficWidget";
// v1.14.59 — Import rimosso: import LeadsQuickTab from "@/components/LeadsQuickTab";
import FunnelAnalyticsTab from "@/components/FunnelAnalyticsTab";
import LlmCostsTab from "@/components/LlmCostsTab";

// CRM Multi-Compagnia Fase 2 (feb 2026) — 10 nuove pagine
import CmDashboard from "@/pages/admin/crm-multi/Dashboard";
import CmCompagnie from "@/pages/admin/crm-multi/CompagnieList";
import CmProdotti from "@/pages/admin/crm-multi/ProdottiList";
import CmClienti from "@/pages/admin/crm-multi/ClientiList";
import CmPolizze from "@/pages/admin/crm-multi/PolizzeList";
import CmScadenze from "@/pages/admin/crm-multi/ScadenzeList";
import CmIncassi from "@/pages/admin/crm-multi/IncassiList";
import CmInsoluti from "@/pages/admin/crm-multi/InsolutiList";
import CmSinistri from "@/pages/admin/crm-multi/SinistriList";
import CmSubagenti from "@/pages/admin/crm-multi/SubagentiList";
import CmAuditLog from "@/pages/admin/crm-multi/AuditLog";
// CRM Multi-Compagnia Fase 3 (feb 2026) — Import SIA Omnia 8
import SiaHub from "@/pages/admin/crm-multi/sia/SiaHub";
import SiaChecklist from "@/pages/admin/crm-multi/sia/SiaChecklist";
import SiaImportWizard from "@/pages/admin/crm-multi/sia/SiaImportWizard";
import SiaBatches from "@/pages/admin/crm-multi/sia/SiaBatches";
import SiaReconcile from "@/pages/admin/crm-multi/sia/SiaReconcile";
import { ToastContainer as CmToastContainer } from "@/components/crm-multi/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAMI = [
  { id: "all", label: "Tutti i Rami", icon: Inbox, color: "text-zinc-600" },
  { id: "auto", label: "Auto", icon: Car, color: "text-blue-600" },
  { id: "casa", label: "Casa", icon: Home, color: "text-amber-600" },
  { id: "vita", label: "Vita", icon: Heart, color: "text-red-500" },
  { id: "aziende", label: "Aziende", icon: Building2, color: "text-purple-600" },
  { id: "sanitario", label: "Sanitario", icon: Stethoscope, color: "text-emerald-600" },
  { id: "viaggio", label: "Viaggi", icon: Plane, color: "text-sky-500" },
  { id: "economico_giuridico", label: "Eco/Giuridico", icon: Briefcase, color: "text-indigo-600" },
  { id: "area_tecnica", label: "Area Tecnica", icon: Shield, color: "text-orange-600" },
  { id: "droni", label: "Droni", icon: Shield, color: "text-cyan-600" },
  { id: "struttura_sanitaria", label: "Struttura San.", icon: Building2, color: "text-teal-600" },
];

const STATUS_CONFIG = {
  nuovo: { label: "Nuovo", icon: AlertCircle, color: "bg-amber-50 text-amber-700 border-amber-200" },
  in_lavorazione: { label: "In Lavorazione", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
  completato: { label: "Completato", icon: CheckCircle, color: "bg-green-50 text-green-700 border-green-200" },
};

// FASE 2d (27-mag-2026 · v1.14.14) — Sidebar refactor: 7 macro → 6 macro,
// 57 voci PRESERVATE. Voci spostate per logica funzionale:
//  • 5 SIA-* spostate da Multi-Compagnia → Operatività (gestionale day-by-day SIA)
//  • 10 cm_* (escluse audit/sync) spostate da Multi-Compagnia → Clienti
//  • cm_audit + polizzaai_sync spostati da Multi-Compagnia → Compliance & Sicurezza
//  • Macro "Multi-Compagnia" eliminata (vuota dopo i ricollocamenti)
// REGOLA ASSOLUTA: nessuna voce rimossa. URL React invariati.
const NAV_GROUPS = [
  {
    id: "operativita", macroIcon: Inbox, color: "from-sky-500 to-blue-600",
    label: "📥 CRM — Richieste & Operatività",
    items: [
      // FASE L1 — Nuova home operatore semplificata (in cima, prominente)
      // HOTFIX 27-lug: rimosso externalRoute per renderizzare inline nel CRM hub (tab embedded)
      { id: "cockpit", label: "🎯 Cockpit Operatore", icon: Sparkles, description: "Home unificata: stream/kanban/tabella + Cmd+K search globale + azioni inline (sostituisce Richieste + Convenzioni CRM)" },
      // FASE L2.3 — voci "Richieste Unificate" e "Convenzioni CRM" consolidate nel Cockpit.
      { id: "pratiche_do_lista", label: "📄 Pratiche D&O compilate", icon: FileText, description: "Elenco completo pratiche D&O inviate dai clienti dal sito pubblico (source public_open_v3) — download AIG/Reale/Lloyd's/ZIP", externalRoute: "/admin/pratiche-do" },
      { id: "pratiche_cyber_lista", label: "🛡 Polizze Cyber", icon: FileText, description: "Elenco completo pratiche Cyber Risk (AIG Cyber, Reale Mutua FUR5166, IPR Lloyd's) — download PDF singoli + ZIP + Stop Assuntivo detection", externalRoute: "/admin/pratiche-cyber" },
      { id: "pratiche_fine_arts_lista", label: "🎨 Pratiche Fine Arts", icon: FileText, description: "Elenco pratiche Lloyd's Fine Arts (Chiodo a Chiodo / Personal Collections) — proposta pixel-perfect Lloyd's + thumbnail 4 pagine", externalRoute: "/admin/pratiche-fine-arts" },
      { id: "feature_flags", label: "🎛 Feature Flags", icon: FileText, description: "Attiva/disattiva moduli (Noleggio, futuri). Il codice rimane in produzione — flip del toggle per riaccendere.", externalRoute: "/admin/feature-flags" },
      { id: "crm_renewals", label: "Rinnovi", icon: RefreshCw, description: "Scadenze T-30/T-15/T-7 + task auto" },
      { id: "crm_eventi", label: "Eventi Promozionali", icon: PartyPopper, description: "Landing event-based + lead capture (eventi, fiere, congressi)" },
    ],
  },
  {
    id: "clienti", macroIcon: Users, color: "from-emerald-500 to-emerald-700",
    label: "👥 Clienti & Polizze",
    items: [
      { id: "cm_dashboard", label: "Dashboard portafoglio", icon: Activity, description: "KPI · grafici · scadenze", adminOnly: true },
      { id: "crm_customers", label: "Anagrafica Clienti", icon: Users, description: "Anagrafica + storico richieste/polizze" },
      { id: "cm_clienti", label: "Clienti multi-compagnia", icon: Users, description: "86 record consolidati da crm_customers (FASE 2e)", adminOnly: true },
      { id: "crm_policies", label: "Polizze (legacy)", icon: FileText, description: "Storico polizze legacy (agenzia + concorrenti)" },
      { id: "cm_polizze", label: "Polizze multi-compagnia", icon: FileText, description: "Polizze multi-compagnia + promozione lead", adminOnly: true },
      { id: "cm_scadenze", label: "Scadenze rate", icon: Calendar, description: "Calendario rate da incassare", adminOnly: true },
      { id: "cm_incassi", label: "Incassi", icon: CreditCard, description: "Registrazione e tracking incassi", adminOnly: true },
      { id: "crm_insoluti", label: "Insoluti (legacy)", icon: AlertCircle, description: "Upload CSV insoluti + scheduler reminder" },
      { id: "cm_insoluti", label: "Insoluti multi", icon: AlertCircle, description: "Insoluti aperti con gravità", adminOnly: true },
      { id: "cm_sinistri", label: "Sinistri", icon: Shield, description: "Apertura e gestione sinistri", adminOnly: true },
      { id: "cm_subagenti", label: "Subagenti", icon: Briefcase, description: "Collaboratori e provvigioni", adminOnly: true },
      { id: "crm_intermediari", label: "Intermediari", icon: Briefcase, description: "Sub-agenti RUI + intermediari esterni" },
      { id: "crm_consents", label: "Consensi GDPR", icon: Shield, description: "Gestione consensi + Mandato Digitale" },
      { id: "crm_communications_global", label: "Storico Comunicazioni", icon: Send, description: "WhatsApp + Email globali" },
    ],
  },
  {
    id: "tariffe", macroIcon: ShieldCheck, color: "from-amber-500 to-orange-600",
    label: "🛠 Prodotti & Quotatori",
    items: [
      { id: "quoter", label: "Quoter B2B 🧮", icon: Sparkles, description: "Wizard v2 Composita Reale + futuri prodotti (v1.14.61 agente-gated)", adminOnly: true },
      { id: "do_questionnaire", label: "D&O Multi-Compagnia 📋", icon: FileText, description: "Gestione inviti agente-gated (solo bozze e link cliente). Per l'elenco pratiche compilate dal sito pubblico usa: 📄 Pratiche D&O compilate", adminOnly: true },
      { id: "stime_ricostruzione", label: "Stime VRS", icon: Building2, description: "Lead Stima Costo Ricostruzione (residenziale/commerciale)" },
      { id: "ris_health", label: "Health RiS Tariffa", icon: ShieldCheck, description: "Production-ready RiS · Vicini Impresa", adminOnly: true },
      { id: "realemutua_docs", label: "Set Informativo Reale Mutua", icon: FileText, description: "Set RiS / Vicini Impresa", adminOnly: true },
      { id: "cm_compagnie", label: "Compagnie", icon: Building2, description: "CRUD compagnie + credenziali cifrate Fernet", adminOnly: true },
      { id: "crm_compagnie_partner", label: "Compagnie Partner", icon: Building2, description: "Mandati assicurativi attivi (Reale Mutua, Lloyd's, Elips, UCA…)" },
      { id: "cm_prodotti", label: "Catalogo Prodotti", icon: FileText, description: "Catalogo prodotti per ramo", adminOnly: true },
      { id: "polizzaai_sync", label: "Sync PolizzaAI", icon: RefreshCw, description: "Sincronizzazione prodotti da PolizzaAI", adminOnly: true },
    ],
  },
  {
    id: "comunicazioni", macroIcon: Send, color: "from-violet-500 to-fuchsia-600",
    label: "💬 Comunicazione & Marketing",
    items: [
      { id: "crm_documents", label: "Documenti", icon: FileText, description: "PDF, scansioni, ricevute · 25MB max · soft delete" },
      { id: "crm_payments", label: "Pagamenti Nexi", icon: CreditCard, description: "Pay-by-link · TEST_MODE attivo" },
      { id: "nexi_pagamenti", label: "Transazioni Nexi (storico)", icon: CreditCard, description: "Tutte le transazioni Nexi finalizzate" },
      { id: "pratiche_nlt", label: "Pratiche Noleggio", icon: Car, description: "Pipeline NLT" },
      { id: "pratiche_credito", label: "Pratiche Credito · Ayvens", icon: CreditCard, description: "Pipeline finanziamento Ayvens · 17 PDF precompilati · YouSign" },
      { id: "news_nlt", label: "News NLT", icon: Newspaper, description: "Rubriche noleggio" },
      { id: "rivoluzione", label: "Canale Instagram", icon: Instagram, description: "Video @colucci_assicuratori" },
      { id: "ig_publish", label: "Pubblica su Instagram", icon: Instagram, description: "Pubblicazione diretta video/carosello su IG", adminOnly: true },
      { id: "carosello_export", label: "Genera Carosello", icon: FileDown, description: "Export ZIP Meta" },
      { id: "social_generator", label: "Social Generator (AI)", icon: Sparkles, description: "AI draft + render server-side caroselli brandizzati (v1.14.51)", adminOnly: true },
      { id: "ai_assistant", label: "AI Assistant ⚡", icon: Sparkles, description: "Chat AI interna con tool functions sul CRM (v1.14.58)", adminOnly: true },
    ],
  },
  {
    id: "compliance", macroIcon: Shield, color: "from-rose-500 to-red-600",
    label: "🔒 Sistema & Compliance",
    items: [
      { id: "sia_hub", label: "📥 Migrazione SIA", icon: Database, description: "Import dati da SIA Omnia 8", adminOnly: true },
      { id: "sia_checklist", label: "Checklist Export SIA", icon: FileText, description: "Guida 8 passaggi export", adminOnly: true },
      { id: "sia_import", label: "Wizard Import SIA", icon: Upload, description: "Modalità A — File manuali", adminOnly: true },
      { id: "sia_batches", label: "Storico batch SIA", icon: Activity, description: "Tutti gli import eseguiti", adminOnly: true },
      { id: "sia_reconcile", label: "Quadratura SIA", icon: TrendingDown, description: "Confronto SIA vs CRM", adminOnly: true },
      { id: "compliance_audit", label: "Audit Log", icon: Activity, description: "Eventi compliance + GDPR + sicurezza", adminOnly: true },
      { id: "audit_export_pii", label: "Audit Export PII", icon: ShieldCheck, description: "MINI-FASE B.1 — Tracciamento download CSV con motivazione + SHA256", adminOnly: true, externalRoute: "/admin/audit-log" },
      { id: "kpi_analytics", label: "KPI Analytics", icon: TrendingUp, description: "FASE G — Conversione · tempo medio chiusura · top compagnie cross-ramo (MTD/YTD/custom)", adminOnly: true, externalRoute: "/admin/kpi" },
      { id: "team_analytics", label: "Analytics Team", icon: Users, description: "FASE H3 (STUB) — Performance dipendenti (in dev)", adminOnly: true, externalRoute: "/admin/analytics/team" },
      { id: "admin_utenze", label: "Provisioning Utenze", icon: UserPlus, description: "FASE H — Gestione utenti · ruoli · reset password (solo super_admin)", adminOnly: true, externalRoute: "/admin/utenze" },
      { id: "security_alerts", label: "Alert Sicurezza", icon: ShieldAlert, description: "FASE H — Antifrode 7 regole attive (brute-force, PII spam, role change, CF mismatch...)", adminOnly: true, externalRoute: "/admin/security/alerts" },
      { id: "ai_site_doctor", label: "AI Site Doctor", icon: Activity, description: "Monitoraggio errori full-stack centralizzato (FASE 7)", adminOnly: true },
      { id: "admin_security_hub", label: "Sicurezza Admin (MFA · IP · Log)", icon: ShieldCheck, description: "FASE 8.1 — MFA TOTP + IP allowlist + audit log", adminOnly: true, externalRoute: "/admin/security" },
      { id: "compliance_findings", label: "Compliance Score (FASE 8)", icon: ShieldCheck, description: "Tracking 33 findings audit legal/security", adminOnly: true, externalRoute: "/admin/compliance/findings" },
      { id: "db_backup_hub", label: "Backup Database", icon: ShieldCheck, description: "Dump completo MongoDB single-use, expiry 15 min", adminOnly: true, externalRoute: "/admin/db-backup" },
      { id: "cm_audit", label: "Audit CRM Multi", icon: Activity, description: "Eventi CRM multi-compagnia", adminOnly: true },
      { id: "account_security", label: "Sessioni & OTP", icon: ShieldCheck, description: "Device · OTP · revoca sessioni" },
      { id: "whistleblowing", label: "Whistleblowing", icon: Shield, description: "D.Lgs. 24/2023", adminOnly: true },
      { id: "sicurezza", label: "2FA admin", icon: ShieldCheck, description: "Autenticazione 2FA", adminOnly: true },
    ],
  },
  {
    id: "reporting", macroIcon: Activity, color: "from-cyan-500 to-teal-600",
    label: "📊 Dashboard & Analytics",
    items: [
      { id: "crm_dashboard", label: "Dashboard Executive", icon: Activity, description: "KPI · Funnel · Operatori · Compagnie", adminOnly: true },
      { id: "traffic", label: "Traffic Live", icon: Activity, description: "Attività realtime" },
      { id: "funnel", label: "Funnel & Alert", icon: TrendingDown, description: "Blocchi + AI" },
      { id: "llm_costs", label: "Costi LLM", icon: DollarSign, description: "Spesa Universal Key" , adminOnly: true },
      { id: "gbp", label: "Google Business", icon: MapPin, description: "Sync 3 sedi GBP", adminOnly: true },
    ],
  },
];
// Tab consentiti a role=intermediario (whitelist)
const INTERMEDIARIO_ALLOWED_TABS = new Set([
  "crm_richieste", "crm_customers", "crm_policies", "crm_renewals",
  "crm_communications_global", "crm_documents", "crm_payments",
  "crm_intermediari", "crm_dashboard", "account_security",
  "sicurezza", // setup 2FA obbligatorio al primo login
]);

// Flat array retrocompatibile per i riferimenti esistenti
const TABS = NAV_GROUPS.flatMap(g => g.items);

export default function AdminDashboardPage() {
  const { user, handleLogout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null);
  const [macroSection, setMacroSection] = useState(null);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRamo, setActiveRamo] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [paylinkSub, setPaylinkSub] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/submissions`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setSubmissions(subsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { handleLogout(); navigate("/admin"); }
      else toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  }, [handleLogout, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = submissions.filter(s => {
    if (activeRamo !== "all" && s.type !== activeRamo) return false;
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    if (filterAssigned === "mine" && s.assigned_to !== user?.id) return false;
    if (filterAssigned === "unassigned" && s.assigned_to) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      const c = s.contact || {};
      return (c.nome || "").toLowerCase().includes(t) || (c.cognome || "").toLowerCase().includes(t) || (c.email || "").toLowerCase().includes(t) || s.type.includes(t);
    }
    return true;
  });

  const ramoCount = (ramo) => ramo === "all" ? submissions.length : submissions.filter(s => s.type === ramo).length;

  const assignToMe = async (id) => {
    try {
      await axios.post(`${API}/submissions/${id}/assign`, {}, { withCredentials: true });
      fetchData();
      toast.success("Richiesta presa in carico");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const releaseSubmission = async (id) => {
    try {
      await axios.post(`${API}/submissions/${id}/release`, {}, { withCredentials: true });
      fetchData();
      toast.success("Richiesta rilasciata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/submissions/${id}`, { status: newStatus }, { withCredentials: true });
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
      if (selectedSubmission?.id === id) setSelectedSubmission(p => ({ ...p, status: newStatus }));
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore"); }
  };

  const updateNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/submissions/${id}`, { notes }, { withCredentials: true });
      toast.success("Note salvate");
    } catch { toast.error("Errore"); }
  };

  const openDetail = (sub) => { setSelectedSubmission(sub); setDialogOpen(true); };
  const onLogout = async () => { await handleLogout(); navigate("/admin"); };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
  const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);

  if (loading) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo_bianco.png" alt="Colucci" className="h-7 w-auto invert" />
            <span className="font-heading text-sm font-semibold text-zinc-900">CRM Colucci</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.role === "intermediario" && user?.intermediario_ragione_sociale && (
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
                data-testid="intermediario-badge"
                title={`Codice: ${user?.intermediario_codice || '-'}`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Intermediario: {user.intermediario_ragione_sociale}
              </span>
            )}
            <span className="text-xs text-zinc-500 hidden sm:block">{user?.name || user?.email} <span className="text-zinc-300">|</span> <span className="capitalize text-[#163321] font-medium">{user?.role}</span></span>
            <Button variant="outline" size="sm" onClick={onLogout} className="rounded-full text-xs border-zinc-200" data-testid="admin-logout-btn"><LogOut className="w-3.5 h-3.5 mr-1" /> Esci</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* FASE 3.5b — banner setup 2FA obbligatorio per intermediario */}
        {user?.role === "intermediario" && user?.must_setup_2fa && (
          <div
            className="mb-4 p-4 rounded-xl bg-amber-50 border-2 border-amber-300 flex items-start gap-3"
            data-testid="banner-must-setup-2fa"
          >
            <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 text-sm mb-1">Setup 2FA obbligatorio</h3>
              <p className="text-xs text-amber-800 mb-2">
                Per accedere al portafoglio devi prima attivare l&apos;autenticazione a 2 fattori.
              </p>
              <button
                onClick={() => { setMacroSection("compliance"); setActiveTab("sicurezza"); }}
                className="px-3 py-1.5 rounded-md bg-amber-700 text-white text-xs font-medium hover:bg-amber-800"
                data-testid="btn-go-2fa-setup"
              >
                Configura ora →
              </button>
            </div>
          </div>
        )}

        {/* ===== HOME 6 MACROSEZIONI (quando macroSection === null) ===== */}
        {macroSection === null && (
          <div data-testid="admin-macro-grid">
            {/* Widget "Le mie richieste da lavorare" — solo staff loggato */}
            <MyRichiesteWidget />
            <h2 className="font-heading text-2xl font-semibold text-zinc-900 mb-1">Pannello Colucci</h2>
            <p className="text-sm text-zinc-500 mb-6">Scegli una sezione per iniziare</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {NAV_GROUPS.map((g) => {
                const isIntermediario = user?.role === "intermediario";
                let visible = g.items.filter(i => !i.adminOnly || user?.role === "admin");
                if (isIntermediario) {
                  visible = visible.filter(i => INTERMEDIARIO_ALLOWED_TABS.has(i.id));
                }
                if (!visible.length) return null;
                if (visible.length === 0) return null;
                const MIcon = g.macroIcon || Inbox;
                return (
                  <button key={g.id} onClick={() => { setMacroSection(g.id); setActiveTab(visible[0].id); }}
                    aria-label={`Apri ${g.label}`}
                    className={`group min-h-[148px] p-5 rounded-2xl text-left transition-all shadow-sm hover:shadow-md bg-gradient-to-br ${g.color} text-white relative overflow-hidden`}
                    data-testid={`macro-btn-${g.id}`}>
                    <MIcon className="w-9 h-9 mb-3 opacity-90" />
                    <h3 className="font-heading text-lg font-bold mb-1">{g.label}</h3>
                    <p className="text-xs opacity-80 line-clamp-2">{visible.length} {visible.length === 1 ? "sezione" : "sezioni"} · click per esplorare</p>
                    <ChevronDown className="absolute bottom-3 right-3 w-4 h-4 -rotate-90 opacity-70" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== DETAIL VIEW (macroSection selezionata) ===== */}
        {macroSection !== null && (() => {
          const currentMacro = NAV_GROUPS.find(g => g.id === macroSection);
          const isIntermediario = user?.role === "intermediario";
          let visibleItems = currentMacro?.items.filter(i => !i.adminOnly || user?.role === "admin") || [];
          if (isIntermediario) visibleItems = visibleItems.filter(i => INTERMEDIARIO_ALLOWED_TABS.has(i.id));
          const currentTab = visibleItems.find(t => t.id === activeTab);
          const CIcon = currentTab?.icon || Inbox;
          return (
            <>
              {/* Breadcrumb + indietro */}
              <div className="flex items-center gap-2 mb-4 sticky top-14 bg-zinc-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 z-30 border-b border-zinc-200">
                <button onClick={() => { setMacroSection(null); setActiveTab(null); }}
                  className="px-2 py-1.5 text-xs rounded bg-white border border-zinc-200 hover:bg-zinc-100 flex items-center gap-1"
                  data-testid="macro-back-btn">
                  ← Indietro
                </button>
                <span className="text-xs text-zinc-400">{currentMacro?.label}</span>
                {currentTab && <><span className="text-xs text-zinc-300">›</span><span className="text-xs font-semibold text-zinc-700">{currentTab.label}</span></>}
              </div>

              {/* Tab orizzontali sub-sezioni */}
              <div className="flex gap-1 overflow-x-auto mb-4 pb-1" data-testid="macro-subtabs">
                {visibleItems.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  return (
                    <button key={t.id} onClick={() => {
                      if (t.externalRoute) { navigate(t.externalRoute); return; }
                      setActiveTab(t.id);
                    }} data-testid={`subtab-${t.id}`}
                      className={`whitespace-nowrap px-3 py-2 text-xs rounded-lg flex items-center gap-1.5 transition-all ${isActive ? "bg-[#163321] text-white shadow-sm font-semibold" : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50"}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* CONTENT AREA */}
              <div className="min-w-0" data-testid="admin-content">
        {/* HOTFIX 27-lug — Cockpit Operatore renderizzato INLINE nel CRM hub (tab embedded) */}
        {activeTab === "cockpit" && <AdminCockpitPage embedded />}
        {/* RICHIESTE TAB */}
        {activeTab === "stime_ricostruzione" && (
          <StimaRicostruzioneTab />
        )}

        {/* v1.14.59 — Rimosso handler activeTab="richieste_commercio" (Wizard commercio).
            Le richieste commercio sono accessibili via 'Richieste Unificate' filtro tipologia=commercio. */}

        {activeTab === "richieste" && (
          <div className="flex gap-6">
            {/* Sidebar Rami */}
            <div className="hidden xl:block w-52 shrink-0">
              <div className="bg-white rounded-xl border border-zinc-200 p-2 sticky top-20" data-testid="rami-sidebar">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 px-3 py-2">Rami Assicurativi</p>
                {RAMI.map(r => {
                  const Icon = r.icon;
                  const count = ramoCount(r.id);
                  return (
                    <button key={r.id} onClick={() => setActiveRamo(r.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${activeRamo === r.id ? "bg-[#163321] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                      data-testid={`ramo-${r.id}`}>
                      <Icon className={`w-4 h-4 ${activeRamo === r.id ? "text-white" : r.color}`} />
                      <span className="flex-1 text-left truncate">{r.label}</span>
                      <span className={`text-xs font-medium ${activeRamo === r.id ? "text-white/60" : "text-zinc-400"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Stats row */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {[
                    { l: "Totale", v: stats.total, icon: Inbox, c: "text-zinc-600", bg: "bg-zinc-100" },
                    { l: "Nuove", v: stats.nuovo, icon: AlertCircle, c: "text-amber-600", bg: "bg-amber-50" },
                    { l: "In Lavorazione", v: stats.in_lavorazione, icon: Clock, c: "text-blue-600", bg: "bg-blue-50" },
                    { l: "Completate", v: stats.completato, icon: CheckCircle, c: "text-green-600", bg: "bg-green-50" },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}><s.icon className={`w-4 h-4 ${s.c}`} /></div>
                      <div><p className="text-xs text-zinc-500">{s.l}</p><p className="text-xl font-heading font-bold text-zinc-900">{s.v}</p></div>
                    </div>
                  ))}
                </div>
              )}

              {/* Filters */}
              <div className="bg-white rounded-xl border border-zinc-200 p-3 mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Cerca nome, email..." className="pl-9 rounded-lg border-zinc-200 h-9 text-sm" data-testid="search-input" />
                </div>
                {/* Mobile ramo filter */}
                <Select value={activeRamo} onValueChange={setActiveRamo}>
                  <SelectTrigger className="w-[140px] rounded-lg border-zinc-200 h-9 text-sm lg:hidden" data-testid="mobile-ramo"><SelectValue /></SelectTrigger>
                  <SelectContent>{RAMI.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px] rounded-lg border-zinc-200 h-9 text-sm"><SelectValue placeholder="Stato" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="nuovo">Nuovo</SelectItem>
                    <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                  <SelectTrigger className="w-[140px] rounded-lg border-zinc-200 h-9 text-sm" data-testid="filter-assigned"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="mine">Le mie</SelectItem>
                    <SelectItem value="unassigned">Non assegnate</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg border-zinc-200 h-9 text-xs" data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50/50">
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Data</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Ramo</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Cliente</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Stato</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Assegnato a</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-12 text-sm text-zinc-400">Nessuna richiesta</td></tr>
                      ) : filtered.map(sub => {
                        const ramoConf = RAMI.find(r => r.id === sub.type) || RAMI[0];
                        const RIcon = ramoConf.icon;
                        const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.nuovo;
                        const c = sub.contact || {};
                        const isMine = sub.assigned_to === user?.id;
                        return (
                          <tr key={sub.id} className={`border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors ${isMine ? "bg-[#163321]/[0.02]" : ""}`} data-testid={`row-${sub.id}`}>
                            <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">{fmtDate(sub.created_at)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2"><RIcon className={`w-4 h-4 ${ramoConf.color}`} /><span className="text-sm text-zinc-900">{ramoConf.label}</span></div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-zinc-900 font-medium">{c.nome} {c.cognome}</p>
                              <p className="text-xs text-zinc-400">{c.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}><sc.icon className="w-3 h-3" />{sc.label}</span>
                            </td>
                            <td className="px-4 py-3">
                              {sub.assigned_name ? (
                                <span className={`text-xs font-medium ${isMine ? "text-[#163321]" : "text-zinc-600"}`}>{sub.assigned_name}</span>
                              ) : (
                                <span className="text-xs text-zinc-300">Non assegnata</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                {!sub.assigned_to && (
                                  <Button variant="outline" size="sm" onClick={() => assignToMe(sub.id)} className="text-[#163321] border-[#163321]/20 hover:bg-[#163321]/5 text-xs h-7 px-2" data-testid={`assign-${sub.id}`}>
                                    <Hand className="w-3 h-3 mr-1" /> Prendi
                                  </Button>
                                )}
                                {isMine && (
                                  <Button variant="ghost" size="sm" onClick={() => releaseSubmission(sub.id)} className="text-zinc-400 hover:text-zinc-600 text-xs h-7 px-2" data-testid={`release-${sub.id}`}>
                                    <UserMinus className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => openDetail(sub)} className="text-zinc-500 hover:text-zinc-900 text-xs h-7 px-2" data-testid={`view-${sub.id}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setPaylinkSub(sub)} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 text-xs h-7 px-2" data-testid={`paylink-${sub.id}`} title="Genera link pagamento">
                                  <CreditCard className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-3 text-center">{filtered.length} richiesta/e</p>
            </div>
          </div>
        )}

        {/* WHISTLEBLOWING TAB (admin only) */}
        {activeTab === "whistleblowing" && user?.role === "admin" && <WhistleblowingAdminTab />}
        {activeTab === "realemutua_docs" && user?.role === "admin" && <RealeMutuaDocsTab />}
        {activeTab === "ris_health" && user?.role === "admin" && <RisTariffaHealth />}
        {activeTab === "richieste_unified" && <CRMRichiesteUnifiedPage />}
        {/* v1.14.59 — Rimossi shortcut activeTab "richieste_catnat" e "richieste_commercio_quick":
            entrambi raggiungibili da Richieste Unificate con filtro tipologia. */}
        {activeTab === "crm_richieste" && <CRMLeadsPage />}
        {activeTab === "crm_customers" && <CRMCustomersPage />}
        {activeTab === "crm_policies" && <CRMPoliciesPage />}
        {activeTab === "crm_intermediari" && <CRMIntermediariPage />}
        {activeTab === "crm_compagnie_partner" && <CRMCompagniePartnerPage />}
        {activeTab === "crm_eventi" && <CRMEventiPage />}
        {activeTab === "crm_insoluti" && <CRMInsolutiPage />}
        {activeTab === "crm_renewals" && <CRMRenewalsPage />}
        {activeTab === "crm_tasks" && <CRMTasksPage />}
        {activeTab === "crm_documents" && <CRMDocumentsPage />}
        {activeTab === "crm_payments" && <CRMPaymentsPage />}
        {activeTab === "crm_communications_global" && <CRMCommunicationsPage />}
        {activeTab === "richieste_aziende" && <RichiesteAssicurativeTab />}

        {/* CRM Multi-Compagnia Fase 2 (feb 2026) — 11 nuove sezioni admin */}
        {activeTab === "cm_dashboard" && <CmDashboard />}
        {activeTab === "cm_compagnie" && <CmCompagnie />}
        {activeTab === "cm_prodotti" && <CmProdotti />}
        {activeTab === "cm_clienti" && <CmClienti />}
        {activeTab === "cm_polizze" && <CmPolizze />}
        {activeTab === "cm_scadenze" && <CmScadenze />}
        {activeTab === "cm_incassi" && <CmIncassi />}
        {activeTab === "cm_insoluti" && <CmInsoluti />}
        {activeTab === "cm_sinistri" && <CmSinistri />}
        {activeTab === "cm_subagenti" && <CmSubagenti />}
        {activeTab === "cm_audit" && <CmAuditLog />}
        {activeTab === "sia_hub" && <SiaHub onNavigate={setActiveTab} />}
        {activeTab === "sia_checklist" && <SiaChecklist onBack={() => setActiveTab("sia_hub")} />}
        {activeTab === "sia_import" && <SiaImportWizard onBack={() => setActiveTab("sia_hub")} />}
        {activeTab === "sia_batches" && <SiaBatches />}
        {activeTab === "sia_reconcile" && <SiaReconcile />}

        {/* TRAFFIC LIVE DASHBOARD */}
        {activeTab === "traffic" && (
          <div className="space-y-6 max-w-5xl" data-testid="admin-traffic-tab">
            <div>
              <h3 className="text-lg font-semibold text-zinc-900 mb-1">Attività istantanea del sito</h3>
              <p className="text-sm text-zinc-500">
                Contatore GDPR-compliant: IP hashato con salt giornaliero rotante, nessun cookie, nessun tracking persistente. Dati rolling 35 giorni.
              </p>
            </div>
            <LiveTrafficWidget />
          </div>
        )}

        {/* v1.14.59 — Rimosso tab "leads_quick" (Lead Pop-up widget UI).
            I lead pop-up storici restano accessibili via 'Richieste Unificate' (source=lead-popup). */}

        {/* FUNNEL & ALERT TAB */}
        {activeTab === "funnel" && (
          <div className="max-w-6xl" data-testid="admin-funnel-tab">
            <FunnelAnalyticsTab />
          </div>
        )}

        {/* LLM COSTS TAB */}
        {activeTab === "llm_costs" && user?.role === "admin" && (
          <LlmCostsTab />
        )}

        {/* GBP TAB */}
        {activeTab === "gbp" && user?.role === "admin" && (
          <GbpSyncTab />
        )}

        {/* ITER 72 — Legacy CONVENZIONI TAB rimosso. Redirect diretto a nuovo CRM.
             (Se qualcuno arriva qui tramite deep-link vecchio hash `#convenzioni`,
              lo mandiamo al nuovo dashboard gerarchico.) */}
        {activeTab === "convenzioni" && (
          <Navigate to="/admin/convenzioni/crm" replace />
        )}

        {/* PRATICHE NOLEGGIO TAB */}
        {activeTab === "pratiche_nlt" && <PraticheNoleggioTab />}
        {activeTab === "pratiche_credito" && <PraticheCreditoTab />}

        {/* NEWS NOLEGGIO TAB (rubriche NLT) */}
        {activeTab === "news_nlt" && <NewsNoleggioTab />}

        {/* RIVOLUZIONE INSTAGRAM TAB */}
        {activeTab === "rivoluzione" && <RivoluzioneAdminTab />}

        {/* CAROSELLO EXPORT TAB (Meta Business Suite workflow) */}
        {activeTab === "carosello_export" && <CaroselloExportTab />}

        {/* v1.14.51 — SOCIAL GENERATOR AI (admin-only) */}
        {activeTab === "social_generator" && user?.role === "admin" && <AdminSocialGeneratorPage />}
        {activeTab === "ai_assistant" && user?.role === "admin" && <AdminAIAssistantPage />}
        {activeTab === "quoter" && user?.role === "admin" && <AdminQuoterPage />}
        {activeTab === "do_questionnaire" && user?.role === "admin" && <DOInvitesPanel />}

        {/* INSTAGRAM PUBBLICAZIONE API TAB */}
        {activeTab === "ig_publish" && user?.role === "admin" && <InstagramPublishTab />}

        {/* +SIMPLE DIAGNOSTIC TAB */}
        {activeTab === "nexi_pagamenti" && <NexiTransactionsTab />}
        {activeTab === "polizzaai_sync" && user?.role === "admin" && <PolizzaAISyncTab />}

        {activeTab === "sicurezza" && (user?.role === "admin" || user?.role === "intermediario") && (
          <>
            <SiteAccessGateToggle />
            <TwoFactorSettings />
          </>
        )}

        {activeTab === "account_security" && <AccountSecurityPage />}
        {activeTab === "compliance_audit" && user?.role === "admin" && <ComplianceAuditPage />}
        {activeTab === "ai_site_doctor" && user?.role === "admin" && <AdminAISiteDoctorPage />}
        {activeTab === "crm_consents" && <CRMConsentsPage />}
        {activeTab === "crm_dashboard" && <CRMDashboardPage />}
              </div>{/* admin-content */}
            </>
          );
        })()}
      </div>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="detail-dialog">
          {selectedSubmission && <SubmissionDetail sub={selectedSubmission} onUpdateStatus={updateStatus} onUpdateNotes={updateNotes} fmtDate={fmtDate} user={user} onAssign={assignToMe} onRelease={releaseSubmission} onPayLink={() => setPaylinkSub(selectedSubmission)} />}
        </DialogContent>
      </Dialog>

      {/* Payment Link Generator Modal */}
      {paylinkSub && (
        <PaymentLinkGenerator submission={paylinkSub} onClose={() => setPaylinkSub(null)} />
      )}

      {/* CRM Multi-Compagnia toast container */}
      <CmToastContainer />
    </div>
  );
}

function BlackHoleSink() { return null; }

/* ========== SUBMISSION DETAIL (full field recap) ========== */
function SubmissionDetail({ sub, onUpdateStatus, onUpdateNotes, fmtDate, user, onAssign, onRelease, onPayLink }) {
  const [notes, setNotes] = useState(sub.notes || "");
  const [showData, setShowData] = useState(true);
  const contact = sub.contact || {};
  const d = sub.data || {};
  const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.nuovo;
  const ramoConf = RAMI.find(r => r.id === sub.type) || RAMI[0];
  const RIcon = ramoConf.icon;
  const isMine = sub.assigned_to === user?.id;

  const dataFields = Object.entries(d).filter(([k]) => !["richiesta"].includes(k));

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-lg flex items-center gap-2">
          <RIcon className={`w-5 h-5 ${ramoConf.color}`} />{ramoConf.label} — {contact.nome} {contact.cognome}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-5 mt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${sc.color}`}><sc.icon className="w-3 h-3" />{sc.label}</span>
          <div className="flex items-center gap-2">
            {sub.assigned_name && <span className="text-xs text-zinc-500">Assegnato: <strong>{sub.assigned_name}</strong></span>}
            <span className="text-xs text-zinc-400">{fmtDate(sub.created_at)}</span>
          </div>
        </div>

        {/* Assignment */}
        <div className="flex gap-2 flex-wrap">
          {!sub.assigned_to && (
            <Button size="sm" onClick={() => onAssign(sub.id)} className="rounded-full bg-[#163321] text-white text-xs"><Hand className="w-3 h-3 mr-1" /> Prendi in carico</Button>
          )}
          {isMine && (
            <Button variant="outline" size="sm" onClick={() => onRelease(sub.id)} className="rounded-full text-xs"><UserMinus className="w-3 h-3 mr-1" /> Rilascia</Button>
          )}
          {onPayLink && (
            <Button size="sm" onClick={onPayLink} className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs" data-testid="detail-paylink-btn">
              <CreditCard className="w-3 h-3 mr-1" /> Genera link pagamento
            </Button>
          )}
        </div>

        {/* Contact */}
        <div className="bg-zinc-50 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-zinc-500 text-xs">Nome</span><p className="text-zinc-900 font-medium">{contact.nome} {contact.cognome}</p></div>
          <div><span className="text-zinc-500 text-xs">Email</span><p className="text-zinc-900">{contact.email}</p></div>
          <div><span className="text-zinc-500 text-xs">Telefono</span><p className="text-zinc-900">{contact.telefono}</p></div>
          <div><span className="text-zinc-500 text-xs">Tipo</span><p className="text-zinc-900">{ramoConf.label}</p></div>
        </div>

        {/* All data fields */}
        <div>
          <button onClick={() => setShowData(!showData)} className="flex items-center gap-2 text-sm font-medium text-[#163321] hover:underline mb-3" data-testid="toggle-data">
            {showData ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Tutti i campi del questionario ({dataFields.length})
          </button>
          {showData && (
            <div className="bg-zinc-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {dataFields.map(([key, val]) => (
                <div key={key} className="flex justify-between py-1 border-b border-zinc-100 last:border-0">
                  <span className="text-xs text-zinc-500 capitalize">{key.replace(/_/g, " ")}</span>
                  <span className="text-xs text-zinc-900 font-medium text-right max-w-[55%] truncate">
                    {typeof val === "object" ? JSON.stringify(val) : String(val || "-")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Files */}
        {sub.files && sub.files.length > 0 && (
          <div>
            <p className="text-sm font-medium text-zinc-900 mb-2">Documenti ({sub.files.length})</p>
            <div className="space-y-1">{sub.files.map((f, i) => (
              <div key={f.id || i} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg text-xs"><FileText className="w-3 h-3 text-zinc-400" /><span>{f.content_type || "Documento"}</span></div>
            ))}</div>
          </div>
        )}

        {/* Stima Valore di Ricostruzione (silente, FASE 10C.2) — solo per type=casa */}
        {sub.type === "casa" && (
          <VrsDetailBlock
            data={sub}
            onRicalcola={async () => {
              try {
                const { data: r } = await axios.post(
                  `${API}/admin/casa/${sub.id}/ricalcola-vrs`,
                  {},
                  { withCredentials: true }
                );
                Object.assign(sub, {
                  valore_ricostruzione_stimato: r.valore_ricostruzione_stimato,
                  valore_ricostruzione_audit: r.audit,
                });
                toast.success(r.outcome === "ok" ? "Stima aggiornata" : `Skip: ${r.reason}`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Errore ricalcolo VRS");
              }
            }}
          />
        )}

        {/* Task A (8 mag 2026): VRS silente per type=aziende — sede operativa */}
        {sub.type === "aziende" && (
          <VrsDetailBlock
            data={{
              valore_ricostruzione_stimato: sub.data?.valore_ricostruzione_stimato,
              valore_ricostruzione_audit: sub.data?.valore_ricostruzione_audit,
            }}
            title="Stima valore di ricostruzione sede operativa (auto)"
            onRicalcola={async () => {
              try {
                const { data: r } = await axios.post(
                  `${API}/admin/aziende/${sub.id}/ricalcola-vrs`,
                  {},
                  { withCredentials: true }
                );
                if (!sub.data) sub.data = {};
                sub.data.valore_ricostruzione_stimato = r.valore_ricostruzione_stimato;
                sub.data.valore_ricostruzione_audit = r.audit;
                toast.success(r.outcome === "ok" ? "Stima aggiornata" : `Skip: ${r.reason}`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Errore ricalcolo VRS");
              }
            }}
          />
        )}

        {/* Status update */}
        <div className="border-t border-zinc-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-zinc-900">Aggiorna stato</p>
          <div className="flex gap-2">
            {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
              <Button key={key} variant={sub.status === key ? "default" : "outline"} size="sm"
                className={`rounded-full text-xs ${sub.status === key ? 'bg-[#163321] text-white' : 'border-zinc-200'}`}
                onClick={() => onUpdateStatus(sub.id, key)} disabled={sub.status === key}>
                <conf.icon className="w-3 h-3 mr-1" />{conf.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-900">Note interne</p>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="rounded-xl border-zinc-200 min-h-[80px] text-sm" placeholder="Aggiungi note..." data-testid="notes-textarea" />
          <Button size="sm" onClick={() => onUpdateNotes(sub.id, notes)} className="rounded-full bg-[#163321] text-white text-xs" data-testid="save-notes">Salva note</Button>
        </div>
      </div>
    </>
  );
}


